import type { SOPDetailDTO } from "@/modules/sop/domain/contracts";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";

interface SOPPdfPreviewContentProps {
  sop: SOPDetailDTO;
}

export function SOPPdfPreviewContent({ sop }: SOPPdfPreviewContentProps) {
  return (
    <div
      id="sop-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">
            Standard Operating Procedure
          </p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ເອກະສານເລກທີ:{" "}
              <span className="font-normal">{`SOP-${sop.id.slice(-6).toUpperCase()}`}</span>
            </p>
            <p className="mt-1 font-semibold">
              ສະບັບທີ: <span className="font-normal">1.0</span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີອັບເດດລ່າສຸດ:</p>
            <p className="font-normal">{formatDateLocal(sop.updatedAt)}</p>
            <p className="mt-2 font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* SOP Title and Status */}
      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          {sop.title}
        </h2>
      </div>

      {/* Basic Information Table */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນພື້ນຖານ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຈຸດປະສົງຂອງ SOP
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {sop.purpose || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ພະແນກທີ່ກ່ຽວຂ້ອງ
              </td>
              <td className="p-4 text-sm">{sop.departmentName || "ບໍ່ໄດ້ກໍານົດ"}</td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຜູ້ຮັບຜິດຊອບ
              </td>
              <td className="p-4 text-sm">
                {sop.responsiblePersonName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີອັບເດດລ່າສຸດ
              </td>
              <td className="p-4 text-sm">{formatDateTimeLocal(sop.updatedAt)}</td>
            </tr>
          </tbody>
        </table>

        {/* Position Tags */}
        {sop.positionTags && sop.positionTags.length > 0 && (
          <>
            <h4 className="mb-3 font-semibold text-gray-700 text-sm uppercase tracking-wide">
              ຕໍາແໜ່ງທີ່ກ່ຽວຂ້ອງກັບ SOP ນີ້
            </h4>
            <div className="flex flex-wrap gap-2">
              {sop.positionTags.map((tag, idx) => (
                <span
                  key={tag.positionId}
                  className="inline-block font-medium text-gray-800 text-sm"
                >
                  {tag.positionName}
                  {idx < (sop.positionTags?.length ?? 0) - 1 ? "," : "."}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Purpose */}
      {sop.purpose && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            2. ຈຸດປະສົງ
          </h3>
          <p className="font-medium text-base text-gray-800 leading-relaxed">
            {sop.purpose}
          </p>
        </div>
      )}

      {/* Scope */}
      {sop.scope && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            3. ຂອບເຂດການໃຊ້ງານ
          </h3>
          <p className="whitespace-pre-line text-base text-gray-800 leading-relaxed">
            {sop.scope}
          </p>
        </div>
      )}

      {/* Steps */}
      {sop.steps && sop.steps.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            4. ຂັ້ນຕອນການເຮັດວຽກ
          </h3>

          <div className="space-y-6">
            {sop.steps.map((step, index) => (
              <div key={step.id} className="relative">
                {/* Step Header */}
                <div className="mb-3">
                  <h4 className="font-bold text-gray-800 text-lg">
                    4.{step.stepNumber}. {step.title}
                  </h4>
                </div>

                {/* Step Description */}
                {step.description && (
                  <div className="mb-4 ml-6">
                    <div
                      className="text-base text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: step.description }}
                    />
                  </div>
                )}

                {/* Step Responsible Person */}
                {step.responsibleName && (
                  <div className="mb-4 ml-6">
                    <div className="text-sm">
                      <span className="font-semibold text-gray-600">
                        ຜູ້ຮັບຜິດຊອບ:{" "}
                      </span>
                      <span className="text-gray-800">
                        {step.responsibleName}
                      </span>
                    </div>
                  </div>
                )}

                {/* Step Notes/Warnings */}
                {step.notes && (
                  <div className="mb-4 ml-6">
                    <div className="text-sm">
                      <span className="font-semibold text-gray-600">
                        ຂໍ້ຄວນລະວັງພິເສດ:{" "}
                      </span>
                      <span className="text-gray-800">{step.notes}</span>
                    </div>
                  </div>
                )}

                {/* Separator line between steps */}
                {step.stepNumber < (sop.steps?.length ?? 0) && (
                  <div className="mt-6 mb-4 border-gray-200 border-b" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents */}
      {sop.documents && sop.documents.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            5. ເອກະສານ / ແບບຟອມທີ່ໃຊ້
          </h3>

          <div className="overflow-hidden rounded-lg border-2 border-gray-300">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border-gray-300 border-r p-4 text-left font-bold text-gray-700 text-sm uppercase tracking-wide">
                    ລໍາດັບ
                  </th>
                  <th className="border-gray-300 border-r p-4 text-left font-bold text-gray-700 text-sm uppercase tracking-wide">
                    ຊື່ເອກະສານ
                  </th>
                  <th className="p-4 text-left font-bold text-gray-700 text-sm uppercase tracking-wide">
                    ສະຖານະ
                  </th>
                </tr>
              </thead>
              <tbody>
                {sop.documents.map((doc, index) => (
                  <tr key={doc.id} className="border-gray-200 border-t">
                    <td className="border-gray-300 border-r p-4 text-center font-bold text-gray-600">
                      {index + 1}
                    </td>
                    <td className="border-gray-300 border-r p-4">
                      <div>
                        <p className="font-medium text-gray-800">{doc.title}</p>
                        {doc.fileName && (
                          <p className="mt-1 text-gray-500 text-sm">
                            {doc.fileName}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span
                        className={
                          "rounded-full px-3 py-1 font-bold text-xs uppercase tracking-wide"
                        }
                      >
                        {doc.fileUrl ? "ມີໄຟລ໌" : "ບໍ່ມີໄຟລ໌"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Warnings */}
      {sop.warnings && sop.warnings.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 flex items-center gap-3 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            6. ຂໍ້ຄວນລະວັງສຳຄັນ
          </h3>

          <div className="space-y-4">
            {sop.warnings.map((warning, index) => (
              <div className="flex items-start gap-4" key={warning.id}>
                <p className="text-gray-800">
                  6.{index + 1}. {warning.warningText}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer - Signatures and Approval */}
      <div className="mt-16 border-gray-800 border-t-4 pt-8">
        <div className="grid grid-cols-2 gap-8 text-sm">
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານອະນຸມັດໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານທົບທວນໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
        </div>

        <div className="mt-8 border-gray-300 border-t pt-6 text-center text-gray-500 text-xs">
          <p className="font-medium">
            ເອກະສານນີ້ໄດ້ຮັບການສ້າງຈາກລະບົບຈັດການ SOP - FREEDOM Sole Co.Ltd
          </p>
          <p className="mt-2">
            ເລກເອກະສານ: {`SOP-${sop.id.slice(-6).toUpperCase()}`} | ພິມເມື່ອ:{" "}
            {formatDateLocal(new Date())}
          </p>
        </div>
      </div>
    </div>
  );
}
